import type { WireUserRole } from '@logistics/contracts';

/**
 * Path routes: `/overview`, `/sources/add`, `/activity?type=error`. The host
 * serves index.html for any path that is not a file (docs/deployment.md, and
 * Vite in development), so a reload or a shared link lands on the same screen.
 */
export const PAGES = [
  'overview',
  'chat',
  'sources',
  'users',
  'activity',
  'agent',
  'schedules',
  'health',
] as const;
export type PageId = (typeof PAGES)[number];

export interface Route {
  readonly page: PageId;
  /** What to open on arrival, e.g. `add` for the add-source modal. */
  readonly action?: string;
  /** `?type=error&page=2` — a page's filters and position, kept across reloads. */
  readonly query?: Readonly<Record<string, string>>;
}

export const HOME: Route = { page: 'overview' };

const LABELS: Readonly<Record<PageId, string>> = {
  overview: 'Overview',
  chat: 'Chat',
  sources: 'Sources',
  users: 'Users',
  activity: 'Activity',
  agent: 'Agent',
  schedules: 'Schedules',
  health: 'Health',
};

export function isPage(value: string): value is PageId {
  return (PAGES as readonly string[]).includes(value);
}

/** `path` is the pathname, `search` the query string with or without its `?`. */
export function parseLocation(path: string, search = ''): Route {
  const [page = '', action] = path.replace(/^\/+/, '').split('/');
  if (!isPage(page)) return HOME;
  const query = Object.fromEntries(new URLSearchParams(search));
  return {
    page,
    ...(action === undefined || action === '' ? {} : { action }),
    ...(Object.keys(query).length === 0 ? {} : { query }),
  };
}

export function toPath(route: Route): string {
  const path = route.action === undefined ? `/${route.page}` : `/${route.page}/${route.action}`;
  const search = new URLSearchParams(
    Object.entries(route.query ?? {}).filter(([, value]) => value !== ''),
  ).toString();
  return search === '' ? path : `${path}?${search}`;
}

/**
 * An address from when routes were hashes — a bookmark, an old link: `#/chat/…`
 * becomes `/chat/…`. Undefined for anything else.
 */
export function fromLegacyHash(hash: string): string | undefined {
  return hash.startsWith('#/') ? hash.slice(1) : undefined;
}

/**
 * What each role may open (docs/adr/0001-web-auth-and-chat.md, role matrix).
 * Chat needs a dispatcher, and Schedules an admin — a schedule can broadcast to
 * everyone. Users and Agent stay in everybody's nav; the pages themselves hide
 * what only an admin may do.
 */
export function canOpen(page: PageId, role: WireUserRole): boolean {
  if (page === 'chat') return role !== 'viewer';
  if (page === 'schedules') return role === 'admin';
  return true;
}

export interface NavItem {
  readonly page: PageId;
  readonly label: string;
}

export function navItems(role: WireUserRole): NavItem[] {
  return PAGES.filter((page) => canOpen(page, role)).map((page) => ({
    page,
    label: LABELS[page],
  }));
}

/** A route this role may not open falls back to the overview rather than a dead end. */
export function allowedRoute(route: Route, role: WireUserRole): Route {
  return canOpen(route.page, role) ? route : HOME;
}

export function pageLabel(page: PageId): string {
  return LABELS[page];
}

/** The phone's bottom tab bar (design L750): five pages; Users and Agent live in the header. */
const TABS: readonly PageId[] = ['overview', 'chat', 'sources', 'activity', 'health'];

export interface TabItem extends NavItem {
  /** An error count, shown on Health; 0 hides it. */
  readonly badge: number;
}

/** The tabs this role may open; Health carries the same error count as its nav badge. */
export function tabItems(role: WireUserRole, healthErrors: number): TabItem[] {
  return TABS.filter((page) => canOpen(page, role)).map((page) => ({
    page,
    label: LABELS[page],
    badge: page === 'health' ? healthErrors : 0,
  }));
}
